import { ReactElement } from 'react'
import { useParams } from 'react-router-dom'
import { NavLink } from 'react-router-dom'
import { FormattedMessage, useIntl, defineMessages } from 'react-intl'
import { Formik } from 'formik'

import { Button } from 'components/v2/Button'
import Heading from 'components/v2/Heading'
import { Row } from '@10xLabs/react-web/components/Row'
import { Column } from '@10xLabs/react-web/components/Column'
import { Input } from 'components/Input'
import { Textarea } from 'components/v2/Textarea'
import FormLayout from 'features/App/components/FormLayout'
import { schema } from 'features/Loyalty/views/NewRule/schema'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import styled from 'styled-components'
import { InputNumber } from 'components/InputNumber'
import { RewardAutocomplete } from 'features/Loyalty/components/RewardAutocomplete'
import { centsToInteger } from 'utils/number'
import { isNumber } from 'lodash'
import { ICreateRuleFormProps, ICreateRuleForm, ICreateRuleProps } from './types'
import { IReward } from 'features/Loyalty/types'
import RuleCitySegments from '../CitySegments'
import { SelectSalesChannelsV2 } from 'features/Services/components/SelectSalesChannelsV2'
import { ISalesChannel } from 'features/Services/components/NewServicesV2/components/OfferSettingsCard/types'
import { GTRouteServiceSuggestion, GTRouteSuggestion } from 'gql/types.generated'
import { MultiRouteAutocomplete } from 'features/Routes/components/MultiRouteAutocomplete'
import { MultiRouteServiceAutocomplete } from 'features/Services/components/MultiRouteServiceAutocomplete'

const messages = defineMessages({
  ruleNamePlaceholder: {
    id: 'loyalty.campaign.rule.new.name.placeholder',
    defaultMessage: 'Enter rule name'
  },
  ruleDescriptionPlaceholder: {
    id: 'loyalty.campaign.rule.new.description.placeholder',
    defaultMessage: 'Enter description'
  },
  ruleDetailsPlaceholder: {
    id: 'loyalty.campaign.rule.new.details.placeholder',
    defaultMessage: 'Enter rule details'
  },
  ruleHowToGetItPlaceholder: {
    id: 'loyalty.rewards.new.how-to-get-it.placeholder',
    defaultMessage: 'Enter how to get the reward '
  },
  ruleValidAtPlaceholder: {
    id: 'loyalty.rewards.new.valid-at.placeholder',
    defaultMessage: 'Enter how make valid the rule'
  },
  salesChannels: {
    id: 'loyalty.rewards.conditions.salesChannel.placeholder',
    defaultMessage: 'Sales Channel'
  },
  totalBookingValue: {
    id: 'loyalty.rewards.conditions.totalBookingValue.placeholder',
    defaultMessage: 'Total booking value'
  },
  bookingTripCityOrigin: {
    id: 'loyalty.rewards.conditions.bookingTripCityOrigin.placeholder',
    defaultMessage: 'Select an origin'
  },
  bookingTripCityDestination: {
    id: 'loyalty.rewards.conditions.bookingTripCityDestination.placeholder',
    defaultMessage: 'Select a destination'
  },
  bookingTripSegmentOrigin: {
    id: 'loyalty.rewards.conditions.bookingTripSegmentOrigin.placeholder',
    defaultMessage: 'Select an origin'
  },
  bookingTripSegmentDestination: {
    id: 'loyalty.rewards.conditions.bookingTripSegmentDestination.placeholder',
    defaultMessage: 'Select a destination'
  },
  reward: {
    id: 'loyalty.rewards.effects.reward.placeholder',
    defaultMessage: 'Select a reward'
  },
  selectRoutes: {
    id: 'loyalty.rewards.conditions.routes.placeholder',
    defaultMessage: 'Select routes'
  },
  selectServices: {
    id: 'loyalty.rewards.conditions.services.placeholder',
    defaultMessage: 'Select services'
  }
})

const Split = styled(FormLayout.Split)`
  margin-bottom: 20px;
`

const CreateRuleForm = (props: ICreateRuleFormProps): ReactElement => {
  const {
    values,
    touched,
    errors,
    handleChange,
    setFieldValue,
    handleBlur,
    handleSubmit,
    error,
    loading,
    isEditView,
    companyId,
    isCampaignStarted,
    dirty,
    oldLoyaltyCampaignRule
  } = props
  const intl = useIntl()
  const { campaignId } = useParams()

  return (
    <form autoComplete="false" onSubmit={handleSubmit}>
      <FormLayout.Content>
        <FormLayout.CardContent>
          <Row>
            <Column width="50%">
              <Heading.H5>
                <FormattedMessage id="rules.new.title.rule" defaultMessage="Rule" />
              </Heading.H5>
            </Column>
          </Row>
          <Row>
            <Column width="50%">
              <Input
                id="ruleName"
                value={values.ruleName}
                onChange={handleChange}
                placeholder={intl.formatMessage(messages.ruleNamePlaceholder)}
                label={<FormattedMessage id="loyalty.campaign.rule.new.name" defaultMessage="Rule Name" />}
                onBlur={handleBlur}
                touched={touched.ruleName}
                errors={errors.ruleName}
                disabled={isEditView}
              />
            </Column>
            <Column width="50%">
              <Textarea
                id="ruleDescription"
                value={values.ruleDescription}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched.ruleDescription}
                errors={errors.ruleDescription}
                placeholder={intl.formatMessage(messages.ruleDescriptionPlaceholder)}
                label={<FormattedMessage id="loyalty.rules.new.description" defaultMessage="Description" />}
                disabled={isEditView}
              />
            </Column>
          </Row>
          <Row>
            <Column>
              <Heading.H5>
                <FormattedMessage id="rules.new.title.detail-settings" defaultMessage="Detail Settings" />
              </Heading.H5>
            </Column>
          </Row>
          <Row>
            <Column width="50%">
              <Textarea
                id="ruleHowToGetIt"
                value={values.ruleHowToGetIt}
                onChange={handleChange}
                placeholder={intl.formatMessage(messages.ruleHowToGetItPlaceholder)}
                label={<FormattedMessage id="loyalty.campaign.rule.new.how-to-get-it" defaultMessage="How to get it" />}
                onBlur={handleBlur}
                touched={touched.ruleHowToGetIt}
                errors={errors.ruleHowToGetIt}
              />
            </Column>
            <Column width="50%">
              <Textarea
                id="ruleDetails"
                value={values.ruleDetails}
                onChange={handleChange}
                placeholder={intl.formatMessage(messages.ruleDetailsPlaceholder)}
                label={<FormattedMessage id="loyalty.campaign.rule.new.details" defaultMessage="Details" />}
                onBlur={handleBlur}
                touched={touched.ruleDetails}
                errors={errors.ruleDetails}
              />
            </Column>
          </Row>
          <Row>
            <Column width="50%">
              <Textarea
                id="ruleValidAt"
                value={values.ruleValidAt}
                onChange={handleChange}
                placeholder={intl.formatMessage(messages.ruleValidAtPlaceholder)}
                label={<FormattedMessage id="loyalty.campaign.rule.new.name" defaultMessage="Valid At" />}
                onBlur={handleBlur}
                touched={touched.ruleValidAt}
                errors={errors.ruleValidAt}
              />
            </Column>
          </Row>

          <Split />
          <Row>
            <Column width="50%">
              <Heading.H5>
                <FormattedMessage id="rules.new.title.rule" defaultMessage="Conditions" />
              </Heading.H5>
              <p>
                <FormattedMessage id="rules.new.subtitle.rule" defaultMessage="When all of these are true:" />
              </p>
            </Column>
          </Row>
          <Row>
            <Column width="50%">
              <SelectSalesChannelsV2
                id="salesChannels"
                values={values.salesChannels}
                readOnlyValues={oldLoyaltyCampaignRule?.conditions.salesChannelsV2 ?? []}
                placeholder={intl.formatMessage(messages.salesChannels)}
                onChange={(salesChannels: ISalesChannel[]): void => {
                  setFieldValue('salesChannels', salesChannels)
                }}
                touched={Boolean(touched?.salesChannels)}
                errors={errors?.salesChannels as string}
                label={
                  <FormattedMessage id="loyalty.campaign.rule.conditions.salesChannel" defaultMessage="Sales Channel" />
                }
              />
            </Column>
            <Column width="50%">
              <InputNumber
                id="totalBookingValue"
                onChange={(value: ICreateRuleForm['totalBookingValue']): void => {
                  setFieldValue('totalBookingValue', value)
                }}
                disabled={isEditView && isCampaignStarted}
                onBlur={handleBlur}
                value={values.totalBookingValue}
                placeholder={intl.formatMessage(messages.totalBookingValue)}
                touched={touched.totalBookingValue}
                errors={errors.totalBookingValue}
                maskOptions={{
                  suffix: '',
                  prefix: '',
                  allowDecimal: false
                }}
                label={
                  <FormattedMessage
                    id="loyalty.campaign.rule.conditions.totalBookingValue"
                    defaultMessage="Total Booking Value"
                  />
                }
              />
            </Column>
          </Row>
          <Row>
            <Column width="50%">
              <Heading.Body>
                <FormattedMessage id="rules.new.conditions.citiesSegments" defaultMessage="Cities and Segments" />
              </Heading.Body>
            </Column>
          </Row>
          <RuleCitySegments
            companyId={companyId}
            isEditView={isEditView}
            isCampaignStarted={Boolean(isCampaignStarted)}
          />
          <Row>
            <Column width="50%">
              <Heading.Body>
                <FormattedMessage id="rules.new.conditions.routesServices" defaultMessage="Routes and Services" />
              </Heading.Body>
            </Column>
          </Row>
          <Row>
            <Column>
              <MultiRouteAutocomplete
                id="routes"
                values={values.routes}
                onChange={(routes: GTRouteSuggestion[]): void => {
                  setFieldValue('routes', routes)
                }}
                placeholder={intl.formatMessage(messages.selectRoutes)}
                label={<FormattedMessage id="loyalty.campaign.rule.conditions.routes" defaultMessage="Routes" />}
                uniqueName
              />
            </Column>
          </Row>
          <Row>
            <Column>
              <MultiRouteServiceAutocomplete
                id="services"
                values={values.services}
                onChange={(services: GTRouteServiceSuggestion[]): void => {
                  setFieldValue('services', services)
                }}
                placeholder={intl.formatMessage(messages.selectServices)}
                label={<FormattedMessage id="loyalty.campaign.rule.conditions.services" defaultMessage="Services" />}
                routeIds={values.routes.map(({ id }) => id)}
              />
            </Column>
          </Row>
          <Split />
          <Row>
            <Column width="50%">
              <Heading.H5>
                <FormattedMessage id="rules.new.effects.title" defaultMessage="Effects" />
              </Heading.H5>
              <p>
                <FormattedMessage id="rules.new.effects.subtitle" defaultMessage="Then apply these:" />
              </p>
            </Column>
          </Row>
          <Row>
            <Column width="50%">
              <RewardAutocomplete
                disabled={isEditView}
                id="reward"
                value={values.ruleEffects.reward?.name}
                onSelectReward={(reward: IReward): void => {
                  setFieldValue('ruleEffects.reward', reward)
                }}
                companyId={companyId}
                onBlur={handleBlur}
                placeholder={intl.formatMessage(messages.reward)}
                touched={touched.ruleEffects?.reward?.id}
                errors={errors.ruleEffects?.reward?.id}
                label={<FormattedMessage id="loyalty.campaign.rule.effects.reward" defaultMessage="Reward" />}
              />
            </Column>
          </Row>
        </FormLayout.CardContent>
        <FormLayout.CardFooter>
          {error && (
            <ErrorMessage>
              <FormattedMessage id="login.submit.error" defaultMessage="An error has ocurred" />
            </ErrorMessage>
          )}
          <FormLayout.CardFooterActions>
            <Button as={NavLink} to={`/loyalty/campaigns/${campaignId}/rules`} kind="cancel">
              <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
            </Button>
            <Button type="submit" kind="primary" loading={loading} disabled={!dirty}>
              {isEditView ? (
                <FormattedMessage id="dictionary.update" defaultMessage="Update" />
              ) : (
                <FormattedMessage id="dictionary.next" defaultMessage="Create" />
              )}
            </Button>
          </FormLayout.CardFooterActions>
        </FormLayout.CardFooter>
      </FormLayout.Content>
    </form>
  )
}

export const CreateRule = ({
  loyaltyCampaignRule,
  onSubmit,
  error,
  loading,
  isEditView,
  companyId,
  isCampaignStarted
}: ICreateRuleProps): ReactElement => {
  const intl = useIntl()
  const getIntialValues = (): ICreateRuleForm => {
    const base = {
      ruleName: '',
      ruleDescription: '',
      ruleHowToGetIt: '',
      ruleDetails: '',
      ruleValidAt: '',
      cityOrigins: [],
      cityDestinations: [],
      totalBookingValue: '',
      salesChannels: [],
      segments: [],
      segmentsOrigins: [],
      segmentsDestinations: [],
      ruleEffects: {
        reward: {
          id: '',
          name: ''
        }
      },
      isReturn: false,
      allCitiesAndSegments: false,
      allSegments: false,
      routes: [],
      services: []
    }
    if (isEditView && loyaltyCampaignRule) {
      const totalBookingsValue = loyaltyCampaignRule.conditions?.totalBookingsValue
      return {
        ...base,
        ruleName: loyaltyCampaignRule.name,
        ruleDescription: loyaltyCampaignRule.description ?? '',
        ruleHowToGetIt: loyaltyCampaignRule.howToGet,
        ruleDetails: loyaltyCampaignRule.details,
        ruleValidAt: loyaltyCampaignRule.validAt,
        salesChannels: loyaltyCampaignRule.conditions?.salesChannelsV2 ?? [],
        totalBookingValue: isNumber(totalBookingsValue) ? centsToInteger(totalBookingsValue) : '',
        ruleEffects: {
          reward: {
            id: loyaltyCampaignRule.effects[0]?.reward?.id ?? '',
            name: loyaltyCampaignRule.effects[0]?.reward?.name ?? ''
          }
        },
        cityOrigins:
          loyaltyCampaignRule.conditions.citySegments.origins.map((i) => ({
            ...i,
            readOnly: true
          })) || [],
        cityDestinations:
          loyaltyCampaignRule.conditions.citySegments.destinations.map((i) => ({
            ...i,
            readOnly: true
          })) || [],
        segments: loyaltyCampaignRule.conditions.citySegments.segments || [],
        isReturn: loyaltyCampaignRule.conditions.citySegments.isReturn || false,
        allCitiesAndSegments: loyaltyCampaignRule.allCitiesAndSegments,
        allSegments: loyaltyCampaignRule.allSegments,
        services: loyaltyCampaignRule.conditions.routeServices || []
      }
    }
    return base
  }

  const validationSchema = schema(intl)
  const initialValues = getIntialValues()
  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {(props): ReactElement => {
        return (
          <CreateRuleForm
            {...props}
            isEditView={Boolean(isEditView)}
            isCampaignStarted={Boolean(isCampaignStarted)}
            error={error}
            loading={loading}
            companyId={companyId}
            oldLoyaltyCampaignRule={loyaltyCampaignRule}
          />
        )
      }}
    </Formik>
  )
}
